import { baseApi } from '@/services/api';
import type {
  Event,
  EventCategory,
  Venue,
  CreateEventRequest,
  ManualEventRequest,
  UpdateEventRequest,
  EventsResponse,
  GetEventsParams,
} from './eventsTypes';
const user = localStorage.getItem('authUser') ? JSON.parse(localStorage.getItem('authUser')!) : null;
const userRole = user?.userType || null;
export const eventsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ Get Events (with proper params)
    getEvents: builder.query<EventsResponse, GetEventsParams | any>({
      query: (params = {}) => ({
        url: `/events?${params}`,
        method: 'GET',

      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ event_id }) => ({
              type: 'Events' as const,
              id: event_id,
            })),
            { type: 'Events', id: 'LIST' },
          ]
          : [{ type: 'Events', id: 'LIST' }],
    }),

    // ✅ Get Single Event
    getEvent: builder.query<Event, string>({
      query: (id) => ({
        url: `/events/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Events', id }],
    }),

    // ✅ Create Event (AI-generated)
    createEvent: builder.mutation<Event, CreateEventRequest>({
      query: (event) => ({
        url: '/events',
        method: 'POST',
        body: { data: event },
      }),
      invalidatesTags: [{ type: 'Events', id: 'LIST' }],
    }),

    // ✅ Create Event (Manual)
    createManualEvent: builder.mutation<Event, ManualEventRequest>({
      query: (event) => ({
        url: '/events/manual',
        method: 'POST',
        data: event,
      }),
      invalidatesTags: [{ type: 'Events', id: 'LIST' }],
    }),

    // ✅ Update Event
    updateEvent: builder.mutation<Event, { id: string; data: UpdateEventRequest }>({
      query: ({ id, data }) => ({
        url: `/events/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Events', id },
        { type: 'Events', id: 'LIST' },
      ],
    }),

    // ✅ Delete Event
    deleteEvent: builder.mutation<void, string>({
      query: (id) => ({
        url: `/events/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Events', id: 'LIST' }],
    }),

    // ✅ Get Event Categories
    getEventCategories: builder.query<EventCategory, GetEventsParams | void>({
      query: () => ({
        url: '/event-categories',
        method: 'GET',
      }),
      providesTags: ['EventCategories'],
    }),

    // ✅ Get Venues
    getVenues: builder.query<Venue[], { search?: string; city?: string } | void>({
      query: (params = {}) => ({
        url: '/venues',
        method: 'GET',
        params,
      }),
      providesTags: ['Venues'],
    }),

    // ✅ Get Single Venue
    getVenue: builder.query<Venue, string>({
      query: (id) => ({
        url: `/venues/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Venues', id }],
    }),
    createEventWithSections: builder.mutation<{ data: Event }, any>({
      query: (eventData) => ({
        url: `/custom/${userRole}/event/event`,
        method: "POST",
        data: {
          data: eventData, // Wrap in 'data' for Strapi v4/v5
        },
      }),
      invalidatesTags: [{ type: "Events", id: "LIST" }],
    }),

    // ✅ UPDATE Event with page_sections
    updateEventWithSections: builder.mutation<{ data: Event }, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/custom/${userRole}/event/event/${id}`,
        method: "PUT",
        data: {
          data, // Wrap in 'data' for Strapi
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Events", id },
        { type: "Events", id: "LIST" },
      ],
    }),

    // ✅ GET Event with page_sections populated
    getEventWithSections: builder.query<{ data: Event }, { id: string; populate?: string }>({
      query: ({ id, populate = "*" }) => ({
        url: `/custom/${userRole}/event/event/${id}`,
        method: "POST", // Your API supports POST for complex queries
        data: {
          populate: {
            page_sections: {
              populate: "*", // Populate nested fields in sections
            },
            ai_template: true,
            organization: true,
          },
        },
      }),
      providesTags: (result, error, { id }) => [{ type: "Events", id }],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useLazyGetEventsQuery,
  useGetEventQuery,
  useCreateEventMutation,
  useCreateManualEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetEventCategoriesQuery,
  useGetVenuesQuery,
  useGetVenueQuery,
  useCreateEventWithSectionsMutation,
  useUpdateEventWithSectionsMutation,
  useGetEventWithSectionsQuery,
  useLazyGetEventWithSectionsQuery,
} = eventsAPI;
